/**
 * Product page: gallery switching, variant selection, sticky add-to-cart.
 */

/* ------------------------------------------------------------------ gallery */
/**
 * Scroll-snap gallery: the track is the source of truth. Arrows, thumbs and
 * dots all scroll it; a scroll listener reads back which slide is centred and
 * syncs the active states, so dragging/swiping stays in sync too.
 */
class LJGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.thumbsBox = this.querySelector('[data-thumbs]');
    this.index = 0;

    if (!this.track || this.slides.length === 0) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.show(Number(thumb.dataset.galleryThumb)));
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.show(Number(dot.dataset.galleryDot)));
    });

    // Reference: the rail arrows step the SELECTED image (the rail scrolls as
    // a side effect of keeping the active thumb in view), they are not
    // scroll-only buttons.
    this.querySelectorAll('[data-thumb-scroll]').forEach((btn) => {
      btn.addEventListener('click', () => this.show(this.index + Number(btn.dataset.thumbScroll)));
    });

    // Read the scroll position back so swipes update the active thumb/dot.
    let raf = null;
    this.track.addEventListener(
      'scroll',
      () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.setActive(i);
        });
      },
      { passive: true }
    );

    if (this.thumbsBox) {
      this.thumbsBox.addEventListener('scroll', () => this.toggleThumbNav(), { passive: true });
      // Thumbnails are lazy-loaded, so re-measure once they have laid out.
      window.addEventListener('load', () => this.toggleThumbNav(), { once: true });
    }

    this.setActive(0);
    this.toggleThumbNav();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  /**
   * Advance on a timer, wrapping at the end. Any manual interaction stops it
   * for good, so the carousel never fights the shopper. Honours
   * prefers-reduced-motion and pauses while the tab is hidden.
   */
  startAutoplay() {
    const delay = Number(this.dataset.autoplay || 0);
    if (!delay || this.slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.timer = setInterval(() => {
      if (document.hidden) return;
      const next = this.index + 1 >= this.slides.length ? 0 : this.index + 1;
      this.show(next, { auto: true });
    }, delay);

    // A swipe on the track counts as manual interaction too.
    ['pointerdown', 'wheel', 'touchstart'].forEach((evt) => {
      this.addEventListener(evt, () => this.stopAutoplay(), { once: true, passive: true });
    });
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Jump to a slide, wrapping past either end. The reference swaps images
   * instantly (its track is scroll-behavior: auto), so this uses 'instant'
   * rather than animating the scroll.
   */
  show(index, { auto = false } = {}) {
    // A click/tap means the shopper has taken over — stop advancing.
    if (!auto) this.stopAutoplay();

    const count = this.slides.length;
    const target = ((index % count) + count) % count;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'instant' });
    this.setActive(target);
  }

  setActive(index) {
    this.index = index;

    this.thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    this.updateDots(index);

    // Keep the active thumbnail inside the rail. scrollIntoView() would also
    // scroll the page, so adjust the rail's own scrollTop instead.
    const active = this.thumbs[index];
    if (active && this.thumbsBox) {
      const box = this.thumbsBox;
      const top = active.offsetTop;
      const bottom = top + active.offsetHeight;

      if (top < box.scrollTop) {
        box.scrollTo({ top, behavior: 'smooth' });
      } else if (bottom > box.scrollTop + box.clientHeight) {
        box.scrollTo({ top: bottom - box.clientHeight, behavior: 'smooth' });
      }
    }

    this.toggleThumbNav();
  }

  /**
   * Reference dot strip: at most 5 dots are shown no matter how many slides
   * there are. The window slides to keep the active dot centred, and within
   * it the selected dot is a pill, its neighbours 6px and the outermost 4px
   * — at slide 1 of 10 that reads selected,big,big,small,small.
   */
  updateDots(index) {
    const total = this.dots.length;
    const MAX = 5;

    // Start of the 5-wide window, clamped so it never runs past either end.
    let start = 0;
    if (total > MAX) {
      start = Math.min(Math.max(index - Math.floor(MAX / 2), 0), total - MAX);
    }
    const end = Math.min(start + MAX, total);

    this.dots.forEach((dot, i) => {
      const inWindow = i >= start && i < end;
      dot.hidden = !inWindow;
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');

      // Sized by distance from the selected dot: within 2 renders at 6px,
      // anything further tapers to 4px. At slide 1 of 10 that reproduces the
      // reference's selected,big,big,small,small exactly.
      dot.classList.toggle('is-near', Math.abs(i - index) <= 2);
    });
  }

  /**
   * The rail arrows step through images, so they hide at the ends of the image
   * list — up on the first, down on the last — and stay hidden entirely when
   * every thumbnail already fits.
   */
  toggleThumbNav() {
    const box = this.thumbsBox;
    if (!box) return;

    const overflows = box.scrollHeight > box.clientHeight + 1;

    this.querySelectorAll('[data-thumb-scroll]').forEach((btn) => {
      if (!overflows) {
        btn.hidden = true;
        return;
      }
      const up = Number(btn.dataset.thumbScroll) < 0;
      btn.hidden = up ? this.index === 0 : this.index === this.slides.length - 1;
    });
  }

  /** Jump to the image belonging to a variant after selection changes. */
  showByMediaId(mediaId) {
    if (!mediaId) return;
    const match = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (match >= 0) this.show(match);
  }
}
customElements.define('lj-gallery', LJGallery);

/* ----------------------------------------------------------------- variants */
class LJVariants extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-variant-json]');
    if (!json) return;

    try {
      this.variants = JSON.parse(json.textContent);
    } catch {
      return;
    }

    this.addEventListener('change', () => this.onChange());
  }

  /** Option values in position order, as chosen in the UI. */
  get selectedOptions() {
    const inputs = Array.from(this.querySelectorAll('.pdp__option-input:checked'));
    return inputs
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((input) => input.value);
  }

  onChange() {
    const selected = this.selectedOptions;

    const variant = this.variants.find((v) =>
      v.options.every((opt, i) => opt === selected[i])
    );

    if (!variant) {
      this.setUnavailable();
      return;
    }

    this.updateUrl(variant);
    this.updateForm(variant);
    this.updatePrice(variant);
    this.updateButton(variant);

    const gallery = document.querySelector('lj-gallery');
    if (gallery && variant.featured_media) {
      gallery.showByMediaId(variant.featured_media.id);
    }
  }

  updateUrl(variant) {
    if (!variant || !this.dataset.url) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
  }

  updateForm(variant) {
    const input = document.querySelector('[data-variant-id]');
    if (input) input.value = variant.id;
  }

  updatePrice(variant) {
    document.querySelectorAll('[data-price-current]').forEach((el) => {
      el.textContent = LJVariants.formatMoney(variant.price);
    });

    const onSale = variant.compare_at_price > variant.price;

    // Both the buy box and the sticky bar carry a compare-at node, so update
    // every match rather than just the first.
    document.querySelectorAll('[data-price-compare]').forEach((compare) => {
      if (onSale) {
        compare.textContent = LJVariants.formatMoney(variant.compare_at_price);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    });

    document.querySelectorAll('[data-price-badge]').forEach((badge) => {
      if (onSale) {
        /* Floor, matching the Liquid that renders the badge on load — the
           reference prints 16% on a 16.6% saving. */
        const off = Math.floor(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        badge.textContent = badge.dataset.priceBadge.replace('__PCT__', off);
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    });
  }

  /**
   * Render cents using the shop's own money_format string, so currency
   * symbol, separators and decimals match what Liquid renders server-side.
   */
  static formatMoney(cents) {
    const format = window.LJMoneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return String(cents / 100);

    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const int = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${int}${decimal}${parts[1]}` : int;
    };

    let value;
    switch (placeholder[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder[0], value);
  }

  /**
   * Labels come from data- attributes rendered through Liquid's `t` filter, so
   * a variant change keeps the shop's locale instead of reverting to English.
   */
  updateButton(variant) {
    const btn = document.querySelector('[data-atc]');
    if (!btn) return;

    btn.disabled = !variant.available;
    btn.textContent = variant.available
      ? btn.dataset.labelAdd || btn.textContent
      : btn.dataset.labelSoldOut || btn.textContent;
  }

  setUnavailable() {
    const btn = document.querySelector('[data-atc]');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = btn.dataset.labelUnavailable || btn.textContent;
  }
}
customElements.define('lj-variants', LJVariants);

/* ------------------------------------------------------------- sticky ATC */
(function stickyAtc() {
  const bar = document.querySelector('[data-sticky-atc]');
  const form = document.getElementById('ProductForm');
  if (!bar || !form) return;

  const trigger = bar.querySelector('[data-sticky-atc-btn]');
  if (trigger) {
    trigger.addEventListener('click', () => form.requestSubmit());
  }

  // Submitting through the real "Buy now" button carries its name="checkout",
  // which the global form handler uses to let the request reach Shopify.
  const buyNow = bar.querySelector('[data-sticky-buy-now]');
  if (buyNow) {
    buyNow.addEventListener('click', () => {
      const realBuyNow = form.querySelector('[data-buy-now]');
      if (realBuyNow) realBuyNow.click();
      else form.requestSubmit();
    });
  }

  // Show the bar only once the real ATC button has scrolled out of view
  const atc = form.querySelector('[data-atc]');
  if (!atc || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    },
    { rootMargin: '0px 0px -80px 0px' }
  );

  observer.observe(atc);
})();

/* --------------------------------------------------------------------------
   Share button — native share sheet where the browser has one, clipboard
   fallback everywhere else. Both paths reject when the page is not served over
   https or the user dismisses the sheet, so failures stay silent rather than
   throwing into the console.
   -------------------------------------------------------------------------- */
(() => {
  const btn = document.querySelector('[data-share]');
  if (!btn) return;

  const url = btn.dataset.shareUrl || window.location.href;
  const title = btn.dataset.shareTitle || document.title;

  const flashCopied = () => {
    const original = btn.getAttribute('aria-label');
    btn.classList.add('is-copied');
    btn.setAttribute('aria-label', btn.dataset.labelCopied || 'Link copied');
    setTimeout(() => {
      btn.classList.remove('is-copied');
      if (original) btn.setAttribute('aria-label', original);
    }, 1600);
  };

  btn.addEventListener('click', async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        // AbortError just means the sheet was dismissed; anything else falls
        // through to the clipboard so the button still does something.
        if (err && err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      flashCopied();
    } catch (err) {
      /* no clipboard permission — nothing useful left to try */
    }
  });
})();
